import { H2, P, B, UL, LI, Callout, Table, Steps, Step } from "./ui";

export const VistaManual = () => (
  <>
    <P>
      La producción en 1 clic te lleva de un título a un vídeo sin pasar por ninguna pantalla
      intermedia. La <B>vista manual</B> abre esas pantallas: el guion escena a escena, el material
      de cada una, la composición y la descarga.
    </P>

    <H2 id="cuando">Cuándo usarla</H2>
    <UL>
      <LI>Cuando quieres <B>reescribir</B> parte del guion en vez de aceptarlo entero.</LI>
      <LI>Cuando tienes <B>material propio</B> que quieres meter en el vídeo.</LI>
      <LI>Cuando quieres partir de un guion que ya tenías escrito.</LI>
      <LI>Cuando algo salió mal y quieres arreglar una escena concreta sin rehacer el vídeo.</LI>
    </UL>
    <P>
      Para el uso normal no hace falta: el 1 clic más la parada de revisión cubren la mayoría de
      los casos, y la vista manual es más lenta.
    </P>

    <H2 id="pestanas">Las pestañas</H2>
    <Table
      head={["Pestaña", "Para qué"]}
      rows={[
        ["Guion", "Escribir, pegar, analizar o mejorar el texto escena a escena"],
        ["Material", "Ver y cambiar los clips de cada escena, subir los tuyos"],
        ["Composición", "Estilo visual, música, subtítulos y opciones de montaje"],
        ["Descarga", "El vídeo terminado, la miniatura y la subida a YouTube"],
      ]}
    />

    <Callout tipo="info" titulo="Se puede ir y volver">
      Pasar a vista manual no cancela nada ni pierde el trabajo hecho. Es la misma producción vista
      con más detalle.
    </Callout>
  </>
);

export const Guion = () => (
  <>
    <P>
      El guion se puede generar, pegar o mejorar. Sea cual sea el camino, acaba dividido en escenas
      con su texto narrado y su descripción de lo que se ve.
    </P>

    <H2 id="opciones">Las tres vías</H2>
    <Steps>
      <Step n="1" title="Generar desde un título">
        Lo normal. Se escribe entero a partir del título y la temática del canal.
      </Step>
      <Step n="2" title="Pegar uno tuyo">
        Si ya lo tienes escrito. Se analiza para partirlo en escenas y calcular su duración.
      </Step>
      <Step n="3" title="Mejorar con IA">
        Sobre un guion existente: reescribe buscando mejor ritmo, mejor gancho y mejores
        transiciones entre escenas.
      </Step>
    </Steps>

    <Callout tipo="aviso" titulo="Mejorar cuesta lo mismo que generar">
      «Mejorar con IA» reescribe el guion entero, así que consume como una generación completa. Si
      solo quieres cambiar una frase, edítala a mano: es gratis.
    </Callout>

    <H2 id="storyboard">El storyboard</H2>
    <P>
      Cada escena lleva, además del texto, la lista de planos: qué se ve en pantalla y durante
      cuánto tiempo. Es lo que después se busca como material.
    </P>
    <P>
      Si pegas un guion tuyo, ese storyboard no existe y hay que extraerlo. Ocurre solo, pero es un
      paso con coste: por eso pegar un guion sale algo más caro que generarlo, aunque el texto ya
      esté escrito.
    </P>

    <H2 id="editar">Editar a mano</H2>
    <P>
      Puedes cambiar el texto de cualquier escena directamente. Ten en cuenta que la narración ya
      sintetizada corresponde al texto anterior: si cambias el texto, hay que volver a narrar esa
      escena para que coincidan.
    </P>
  </>
);

export const MaterialManual = () => (
  <>
    <P>
      Desde la pestaña de material ves todas las escenas con sus clips en orden y puedes cambiar
      cualquiera.
    </P>

    <H2 id="sustituir">Sustituir un clip</H2>
    <P>
      Al pulsar sobre uno se busca material alternativo para ese mismo plano. Si ninguna opción
      convence, puedes buscar con tus propias palabras en vez de con la descripción original.
    </P>

    <H2 id="subir">Subir material propio</H2>
    <P>
      Puedes subir tus vídeos e imágenes y colocarlos en la escena que quieras. El límite de tamaño
      por archivo depende de tu plan.
    </P>
    <Callout tipo="truco" titulo="Lo tuyo no se toca">
      Los clips que subes o eliges a mano quedan protegidos: las búsquedas automáticas posteriores
      no los sustituyen. Solo «Regenerar todo» los tira, y por eso conviene evitarlo.
    </Callout>

    <H2 id="cobertura">La cobertura</H2>
    <P>
      Cada escena muestra cuántos segundos de narración tiene cubiertos con material. Para poder
      renderizar hace falta que <B>todas</B> las escenas estén cubiertas al completo: si falta
      material, el render se niega a empezar.
    </P>
    <P>
      Si una escena se queda corta, «Completar lo que falta» busca solo los segundos que faltan sin
      tocar lo que ya está bien.
    </P>
  </>
);

export const Composicion = () => (
  <>
    <P>
      La composición decide el aspecto del vídeo: cómo se ve, cómo suena y qué texto aparece en
      pantalla.
    </P>

    <H2 id="opciones">Qué puedes ajustar</H2>
    <Table
      head={["Ajuste", "Qué hace"]}
      rows={[
        ["Música", "Pista de fondo de la biblioteca, ajustada al tono del vídeo"],
        ["Subtítulos", "Texto sincronizado con la narración, grabado en el vídeo"],
        ["Estilo visual", "Tratamiento de color y textura del conjunto"],
        ["Partículas", "Grano de archivo, polvo y efectos de época"],
        ["Rótulos", "Títulos, datos, citas y capítulos que aparecen sobre la imagen"],
      ]}
    />

    <H2 id="montador">El montaje se decide solo</H2>
    <P>
      No tienes que colocar rótulos ni transiciones a mano. Antes de renderizar se planifica el
      montaje entero: qué rótulo va en qué escena, qué transición entre cuáles, dónde poner
      movimiento de cámara y dónde dejar respirar la imagen.
    </P>
    <P>
      Ese plan se hace con el catálogo completo de recursos, sin ahorrar. La idea es que el vídeo
      tenga ritmo visual y no parezca una sucesión de fotos.
    </P>

    <Callout tipo="info" titulo="Los subtítulos suman retención">
      Mucha gente ve vídeos sin sonido, sobre todo en el móvil. Si dudas, actívalos.
    </Callout>
  </>
);

export const Publicar = () => (
  <>
    <P>
      Cuando el render termina, el vídeo está listo para descargar o para subir directamente a
      YouTube.
    </P>

    <H2 id="miniatura">La miniatura</H2>
    <P>
      Se crea desde esta pantalla, no antes. Puedes generarla con IA, subir la tuya o quedarte con
      el fotograma que se toma automáticamente del propio vídeo.
    </P>
    <P>
      Generar miniaturas con IA tiene coste, así que se hace solo cuando lo pides. Aquí es donde
      tiene sentido decidirlo: con el vídeo delante ya sabes qué imagen lo representa.
    </P>
    <Callout tipo="truco" titulo="La miniatura y el título se eligen juntos">
      Compiten en la misma pantalla del espectador. Si el título ya dice «10 barcos fantasma», la
      miniatura no debería repetirlo por escrito: que muestre el barco.
    </Callout>

    <H2 id="subir">Subir a YouTube</H2>
    <Steps>
      <Step n="1" title="Título y descripción">
        Se rellenan con lo del vídeo, pero puedes cambiarlos antes de subir.
      </Step>
      <Step n="2" title="Visibilidad">
        Público, no listado o privado. También puedes programar la publicación para una fecha.
      </Step>
      <Step n="3" title="Subir">
        El vídeo pasa a tu canal con la miniatura seleccionada.
      </Step>
    </Steps>

    <Callout tipo="aviso" titulo="Comprueba las licencias antes de monetizar">
      Los clips marcados para revisar llevan ese aviso porque su licencia no está verificada. Para
      un vídeo que vaya a monetizarse, sustitúyelos antes de publicar.
    </Callout>

    <H2 id="descargar">Descargar</H2>
    <P>
      También puedes descargar el MP4 y subirlo tú donde quieras. El vídeo se conserva en tu cuenta
      según el almacenamiento de tu plan; los renders más antiguos se van eliminando cuando entran
      nuevos.
    </P>
  </>
);
